import React, { useRef } from "react";

import {
  MotivateEngage,
  StickyMessage,
  StagesOfChange,
  ProgramPromotion,
  ModuleQuizForm,
  ModuleHeader,
  ModuleSection,
  ModuleProvider,
  useModuleState,
  ModuleFooter,
  Tooltips,
} from "../../components";

import warehouseMan from "../../assets/gfx/04_warehouse-man.jpg";
import {
  useAppContextMethods,
  useGlobalUserMethods,
  useGlobalUserState,
} from "~/state";
import { upsertHealthyWorkplace } from "~/util";

interface ModuleProps {}

export const Module2: React.FC<ModuleProps> = ({}) => {
  const { nextModule } = useGlobalUserMethods();
  const user = useGlobalUserState();
  const [state, setState, { handleSectionChange }] = useModuleState();

  const { scrollToTop } = useAppContextMethods();

  const headerRef = useRef<HTMLElement>(null);

  const section1Ref = useRef(null);
  const section2Ref = useRef(null);
  const section3Ref = useRef(null);
  const section4Ref = useRef(null);
  const section5Ref = useRef(null);
  const section6Ref = useRef(null);
  const section7Ref = useRef(null);

  const QUIZ_2 = [
    {
      question:
        "Which of the following strategies would NOT be suitable for employees in pre-contemplation and contemplation stages?",
      options: [
        "Raising awareness about the benefits of breaking up sitting time",
        "Self-monitoring activities such as pedometer wearing to raise awareness of current behaviour",
        "Healthy policies",
        "Supportive environmental changes",
        "Activities such as a circuit class or Pilates classes",
      ],
      answer: [4],
      related: section5Ref.current,
    },
    {
      question:
        "What are the five stages of the behaviour change in the 'Stages of Change' model?",
      options: [
        "Pre-contemplation, contemplation, preparation, action and maintenance",
        "Denial, understanding, accepting, acting and moving forward",
        "Disbelief, reasoning, confronting, progressing and maintaining",
      ],
      answer: [0],
      related: section5Ref.current,
    },
    {
      question: "Which of the following is NOT a sticky message?",
      options: [
        "A story from an employee about their quit smoking journey and how it has changed their life for the better",
        "Did you know that there are nine teaspoons of sugar in one 600 mL bottle of sports drink?",
        "Physical activity is good for health",
        "Did you know that using public transport can boost your physical activity levels by around 15 minutes each day?",
      ],
      answer: [2],
      related: section3Ref.current,
    },
    {
      question:
        "From the following, select four (4) strategies that you could use to engage staff in your program.",
      options: [
        "Make sure all health messages used to engage staff are 'sticky'",
        "Use motivating factors such as weight loss, better sleep and being a good role model for children",
        "Use strategies that will assist employees in all five stages of change",
        "Consult with employees on what they think would help their health at work",
        "Only use strategies that will target staff who are already active",
      ],
      answer: [0, 1, 2, 3],
      related: section3Ref.current,
    },
    {
      question:
        "Which of the following are credible sources of information? You can select more than one answer!",
      options: [
        "Nutrition information from anyone who does NOT have a university degree in nutrition (e.g. a personal trainer)",
        "Research and statistics from a public health organisation",
        "Exercise information from an exercise physiologist",
        "Recipes from an online blog",
        "Nutrition information from a dietitian",
      ],
      answer: [1, 2, 4],
      related: section3Ref.current,
    },
  ];

  return (
    <ModuleProvider onSectionChange={handleSectionChange}>
      <ModuleHeader currentPage={2} totalPages={4} ref={headerRef} />

      <ModuleSection
        id="introduction"
        sectionNumber={1}
        sectionTitle="Introduction"
        className="mb-4"
        ref={section1Ref}
      >
        <div className="page-header block-blueberry">
          <h1>Motivating staff to be healthy</h1>
        </div>
      </ModuleSection>

      <ModuleSection
        id="motivate-and-engage"
        sectionNumber={2}
        sectionTitle="Ways to motivate and engage your staff"
        sectionData={React.useMemo(() => {
          return [
            {
              href:
                "https://cdn.livelighter.com.au/assets/resource/workplace/2021-07-toolkit-for-a-healthier-workplace.pdf",
              title: "Toolkit for a healthier workplace",
              type: "document",
            },
          ];
        }, [])}
        className="mb-4"
        ref={section2Ref}
      >
        <div className="row">
          <div className="col-md-10 mx-auto">
            <h2 className="h1 text-center text-uppercase text-warning mb-4">
              Ways to motivate and engage your staff
            </h2>
            <MotivateEngage />
          </div>
        </div>
      </ModuleSection>

      <ModuleSection
        id="sticky-health-message"
        sectionNumber={3}
        sectionTitle={`Ensuring your health message is "sticky"`}
        sectionData={React.useMemo(() => {
          return [
            {
              href: "/SugaryDrinks/Calculator",
              title:
                "LiveLighter's sugary drinks calculator - great if you're looking at healthy eating",
              type: "link",
            },
          ];
        }, [])}
        className="module2__bg1 section-arrows"
        ref={section3Ref}
        fluid
      >
        <div className="bg-overlay-blueberry-dark text-white px-4 py-5">
          <StickyMessage />
        </div>
      </ModuleSection>

      <ModuleSection
        id="workplace-health-promotion"
        sectionNumber={4}
        sectionTitle="Using workplace health promotion to create behaviour change"
        className="mb-4"
        ref={section4Ref}
      >
        <div className="row">
          <div className="col-md-10 col-lg-8 mx-auto">
            <h2 className="h1 text-warning text-uppercase text-center my-4">
              USING WORKPLACE HEALTH PROMOTION TO CREATE BEHAVIOUR CHANGE
            </h2>
            <img
              className="img-fluid mb-4"
              src={warehouseMan}
              alt="Three people with bikes in a warehouse"
            />
            <div
              css={`
                column-count: 2;
                column-gap: 3rem;
              `}
            >
              <p>
                With the right strategies your workplace health and wellbeing
                program can positively influence all staff, including those who
                may appear disinterested at the start. Strategies such as
                healthy policies and supportive workplace environments can
                influence staff to change their behaviour.
              </p>
              <p>
                There are numerous scientific models which describe how
                behaviour change occurs. One that is particularly useful for
                workplaces is called the “stages of change” model
                <Tooltips id={26} /> which describes the five stages an employee
                might go through when converting an unhealthy behaviour into a
                healthier behaviour.
              </p>
              <p>
                It’s likely that you will have employees within each of the five
                stages, therefore it’s important that a range of strategies are
                developed that cater to the different stages. The challenge is
                to identify strategies that will target and engage those in the
                earlier stages (pre-contemplation and contemplation) as these
                employees are usually the hardest to engage.
              </p>
            </div>
          </div>
        </div>
      </ModuleSection>

      <ModuleSection
        id="stages-of-change"
        sectionNumber={5}
        sectionTitle="The stages of change"
        sectionData={React.useMemo(() => {
          return [
            {
              href:
                "https://cdn.livelighter.com.au/assets/resource/workplace/2021-07-best-practice-guide.pdf",
              title:
                "Best practice guide: Workplace health and wellbeing strategies",
              type: "document",
            },
          ];
        }, [])}
        className="module2__bg2 section-arrows"
        ref={section5Ref}
        fluid
      >
        <div className="bg-overlay-blueberry-dark text-white px-4 py-5">
          <StagesOfChange />
        </div>
      </ModuleSection>

      <ModuleSection
        id="program-promotion"
        sectionNumber={6}
        sectionTitle="Program promotion"
        className="mb-4"
        ref={section6Ref}
      >
        <div className="row">
          <ProgramPromotion />
        </div>
      </ModuleSection>

      <ModuleSection
        id="quiz"
        sectionNumber={6}
        sectionTitle="Quiz"
        className="px-3"
        ref={section7Ref}
      >
        <ModuleQuizForm
          quizHeading={`Answer the following ${
            QUIZ_2.length
          } questions to progress to module ${user.currentModule + 1}`}
          quizKey={2}
          data={QUIZ_2}
          onSuccess={() => {
            upsertHealthyWorkplace({
              firstName: user.firstName,
              lastName: user.lastName,
              email: user.email,
              completedModules: Array.from(
                { length: user.currentModule },
                (v, i) => i + 1,
              ).join(","),
              dateLastUsed: Date.now(),
            }).catch((err) => console.log);
          }}
          onNextClick={(e) => {
            e.preventDefault();
            scrollToTop();
            nextModule();
          }}
          onFail={() => {
            upsertHealthyWorkplace({
              firstName: user.firstName,
              lastName: user.lastName,
              email: user.email,
              dateLastUsed: Date.now(),
            }).catch((err) => console.log);
          }}
        />
      </ModuleSection>

      <ModuleFooter
        moduleNumber={2}
        totalSections={6}
        currentSection={state.currentSection}
        sectionTitle={state.currentSectionTitle}
        sectionData={state.currentSectionData || []}
      />
    </ModuleProvider>
  );
};
